import { Skeleton } from '@/components/atoms/motion/Skeleton';
import { Card } from '@/components/atoms/primitives/card';
import { cn } from '@/components/atoms/primitives/utils';

export interface EventCardSkeletonProps {
  className?: string;
  /** When true, skeleton matches the `w-full` layout variant of `EventCard`. */
  fullWidth?: boolean;
}

/**
 * Presentational loading state for `EventCard`. Mirrors the loaded card
 * (180px cover + title + venue row + date/price row + 44px CTA) so data
 * arrival produces zero layout shift.
 *
 * Shimmer comes from the DS 1.4.0 `Skeleton` motion atom (`.lm-sk` glass
 * sweep, theme-aware + reduced-motion gated in `loci-motion.css`), replacing
 * the previous ad-hoc `animate-pulse` divs (WEB-463). The atom is purely
 * presentational with no `ThemeProvider` dependency, so unit tests still
 * render provider-free.
 */
export function EventCardSkeleton({ className, fullWidth = false }: EventCardSkeletonProps) {
  return (
    <Card
      aria-hidden="true"
      data-testid="event-card-skeleton"
      className={cn(
        'min-h-[400px] overflow-hidden gap-0 rounded-ds-lg',
        fullWidth ? 'w-full' : 'w-[280px]',
        className,
      )}
    >
      <Skeleton variant="block" width="100%" height={180} radius={0} />
      <div className="flex flex-col gap-2 p-4">
        <Skeleton variant="line" width="75%" height={20} />
        <Skeleton variant="line" width="50%" height={20} />
        <div className="flex items-center justify-between gap-2 pt-1">
          <Skeleton variant="line" width={80} height={16} />
          <Skeleton variant="line" width={48} height={16} />
        </div>
        <Skeleton variant="block" width="100%" height={44} radius="var(--r-md)" />
      </div>
    </Card>
  );
}
